import { Box, Button, Grid, IconButton, Stack, Typography } from '@mui/material';
import { Plus, Trash } from '@phosphor-icons/react';
import { TextField } from './TextField';
// Component for the experiences field type
export interface ExperiencesFieldProps {
  field: any;
  name: string;
  label: string;
  helperText?: string;
  required?: boolean;
}

// Experience item structure
export interface Experience {
  title: string;
  description?: string;
  dateFrom: string;
  dateTo?: string;
}

export default function ExperiencesField({
  field,
  name,
  label,
  helperText,
  required,
}: ExperiencesFieldProps) {
  const experiences = field.state.value || [];

  const handleAddExperience = () => {
    const newExperiences = [
      ...experiences,
      { title: '', dateFrom: '', dateTo: '' }
    ];
    field.handleChange(newExperiences);
  };

  const handleRemoveExperience = (index: number) => {
    const newExperiences = [...experiences];
    newExperiences.splice(index, 1);
    field.handleChange(newExperiences);
  };

  const handleExperienceChange = (index: number, propertyName: keyof Experience, value: string) => {
    const newExperiences = [...experiences];
    newExperiences[index] = {
      ...newExperiences[index],
      [propertyName]: value,
    };
    field.handleChange(newExperiences);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle1" gutterBottom>
        {label} {required && <span style={{ color: 'error.main' }}>*</span>}
      </Typography>

      {experiences.map((experience: Experience, index: number) => (
        <Box
          key={index}
          sx={{
            p: 2,
            mb: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Stack direction="row" spacing={2} alignItems='center' justifyContent='space-between'>
                <TextField
                  field={{
                    state: { value: experience.title },
                    getMeta: () => ({ touchedErrors: [] }),
                    handleChange: (value) => handleExperienceChange(index, 'title', value),
                    handleBlur: () => { },
                  }}
                  name={`${name}.${index}.title`}
                  label="Title"
                  required
                />
                <IconButton
                  onClick={() => handleRemoveExperience(index)}
                  color="error"
                  aria-label="remove experience"
                >
                  <Trash size={20} />
                </IconButton>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                field={{
                  state: { value: experience.description || '' },
                  getMeta: () => ({ touchedErrors: [] }),
                  handleChange: (value) => handleExperienceChange(index, 'description', value),
                  handleBlur: () => { },
                }}
                name={`${name}.${index}.description`}
                label="Description (Optional)"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                field={{
                  state: { value: experience.dateFrom },
                  getMeta: () => ({ touchedErrors: [] }),
                  handleChange: (value) => handleExperienceChange(index, 'dateFrom', value),
                  handleBlur: () => { },
                }}
                name={`${name}.${index}.dateFrom`}
                label="From"
                helperText="E.g. June 2025, Summer 2025, 2025"
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                field={{
                  state: { value: experience.dateTo || '' },
                  getMeta: () => ({ touchedErrors: [] }),
                  handleChange: (value) => handleExperienceChange(index, 'dateTo', value),
                  handleBlur: () => { },
                }}
                name={`${name}.${index}.dateTo`}
                label="To"
                helperText="E.g. Current, June 2025, Winter 2025"
              />
            </Grid>
          </Grid>
        </Box>
      ))}

      <Button
        startIcon={<Plus size={20} />}
        onClick={handleAddExperience}
        variant="outlined"
        size="small"
        sx={{ mt: 1 }}
      >
        Add Experience
      </Button>

      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};